import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { BsThreeDotsVertical } from "react-icons/bs";
import config from "./config";
import "./Dashboard.css";
import { useRequest } from "./RequestContext";
import { useNavigate } from "react-router-dom";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";
import { sharedData } from "../sharedData/sharedData";
import { primeDiligenceFromRow } from "../utils/navigationHelper";
import { resetFormCache } from "../utils/resetFormCache";
import { Select } from "./ui/Select";
import { userSession } from "../observers/userObserver";
import { useModal } from "../hooks/useModal";
import ConfirmationModalv2 from "./ui/ConfirmationModal_v2";

const RequestTable = () => {
    const navigate = useNavigate();
    const initialFilter = {
        RequestID: "",
        clientReferenceNumber: "",
        transaction_reference: "",
        client: "",
        property: "",
        DiligenceStatus: "",
        requestStatus: "",
        customer: "",
        Requestor: "",
    };
    const [filter, setFilter] = useState(initialFilter);

    const [selectedDropdownId, setSelectedDropdownId] = useState(null);
    const [userRequests, setUserRequests] = useState([]);
    const { updateRequestId } = useRequest();
    const [page, setPage] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [limit, setLimit] = useState(10);
    const dropdownRef = useRef(null);
    const [selectedToDeleteRequestId, setSelectedToDeleteRequestId] = useState(null);
    const { modalOpen: isDeleteConfirmationModalOpen, setModalOpen: setIsDeleteConfirmationModalOpen } = useModal(false);
    // Sort state: { key: "id"|"client"|"created_at", direction: "asc"|"desc" } or null
    const [sortConfig, setSortConfig] = useState(null);

    const handleSort = (key) => {
        setSortConfig((prev) => {
            if (prev && prev.key === key) {
                if (prev.direction === "asc") return { key, direction: "desc" };
                return null; // third click clears sort
            }
            return { key, direction: "asc" };
        });
    };

    const sortedRequests = useMemo(() => {
        if (!sortConfig) return userRequests;
        const { key, direction } = sortConfig;
        const sorted = [...userRequests].sort((a, b) => {
            let valA = a[key] || "";
            let valB = b[key] || "";
            if (key === "created_at") {
                // Parse dates for comparison
                const dateA = new Date(valA);
                const dateB = new Date(valB);
                if (!isNaN(dateA) && !isNaN(dateB)) return direction === "asc" ? dateA - dateB : dateB - dateA;
            }
            // String comparison (for client and id)
            valA = String(valA).toLowerCase();
            valB = String(valB).toLowerCase();
            if (valA < valB) return direction === "asc" ? -1 : 1;
            if (valA > valB) return direction === "asc" ? 1 : -1;
            return 0;
        });
        return sorted;
    }, [userRequests, sortConfig]);

    const getSortLabel = (key) => {
        if (!sortConfig || sortConfig.key !== key) return "⇅";
        return sortConfig.direction === "asc" ? "▲" : "▼";
    };

    const token = userSession.getToken()

    const fetchUserRequests = useCallback(async () => {
        try {
            
            const response = await fetch(`${config["backend-url"]}/get_filter_request?page=${page}&limit=${limit}`, {
            method: "POST",
            headers: {
              "Authorization": `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify(filter),
          });
      
          const data = await response.json();
          console.log("data", data);
          
            if (response.ok ){
            const requests = data.data.map((req) => ({
              id: req.request_id,
              transactionId: req.transaction_id || req.TransactionId,
               transactionId: req.transaction_id || req.TransactionId,
              transaction: req.transaction_reference || "N/A",
              client: req.company_name || "N/A",
              property: req.property_name || "N/A",
              status: req.Request_Status || "In Progress",
              entityId: req.EntityId,
              created_at: req.created_at || "N/A",
              modified_at: req.modified_at || "N/A",
              Approved_At: req.Approved_At || "N/A",
              Approved_At: req.Approved_At || "N/A",
              propertyId: req.PropertyId,
              customer: req.customer_name || "N/A",
              created_by: req.created_by || "N/A",
              deligenceStatus: req.Deligence_status || "N/A",
              clientReferenceNumber: req.client_reference_number || "N/A",
              // Deligence_status: req.Deligence_status || "N/A",
            }));
console.log("requests", requests);

            setUserRequests(requests); // ✅ update the table
          setTotalRecords(data.pagination.total_items); // ✅ update pagination
          setTotalPages(data.pagination.total_pages);
            } else {
                console.error("Failed to fetch user requests:", data);
            }
        } catch (error) {
            console.error("Error fetching user requests:", error);
        }
    },[limit, page]);

    useEffect(() => {
        fetchUserRequests();
    }, [page, limit, fetchUserRequests]);

    // Same reason as the dashboard: this is a request list, so no request is open. Requests are
    // opened from here too, and without this the previously opened request's cached form values
    // survived and made the next request's sections look validated and locked.
    useEffect(() => {
        sharedData.clear();
        resetFormCache();
    }, []);

    useEffect(() => {
        fetchUserRequests();
    }, [fetchUserRequests]);

    useEffect(() => {
        function handleClickOutside(event) {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setSelectedDropdownId(null);
            }
        }

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    const handleToggleDropdown = (id) => {
        setSelectedDropdownId(selectedDropdownId === id ? null : id);
    };

    const deleteRequest = async (requestId) => {
        try {
            const response = await fetch(`${config["backend-url"]}/delete_request`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({ request_id: requestId }),
            });
            if (response.ok) {
                fetchUserRequests();
            }
        } catch (error) {
            console.error("Error deleting request:", error);
        }
    };

    const handleConfirmDelete = () => {
        setIsDeleteConfirmationModalOpen(false);
        if (selectedToDeleteRequestId) {
            deleteRequest(selectedToDeleteRequestId);
            setSelectedToDeleteRequestId(null);
        }
    };

    // ✅ Styling based on status
    const getStatusClass = (status) => {
        switch (status) {
            case "Completed":
        return "status-closed";
        case "Complete":
          return "status-closed";
      case "deleted":
        return "status-deleted";
      case "In Progress":
        return "status-in-progress";
        case "Pending":
          return "status-in-progress";
      case "Not Started":
        return "status-not-started";
        case "Deleted":
        return "status-Deleted";
        case "Pending MLNO":
          return "status-in-progress";
          case "User Pending":
          return "status-in-progress";
           case "Pending COD":
          return "status-in-progress";
           case "Validated":
          return "status-closed";
          case "Active":
          return "status-closed";
          case "InActive":
        return "status-Deleted";
      default:
        return "";
        }
    };


    const handleSearch = async () => {
        try {
          console.log("filter", filter);
          const response = await fetch(`${config["backend-url"]}/get_filter_request?page=${page}&limit=${limit}`, {
            method: "POST",
            headers: {
              "Authorization": `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify(filter),
          });
      
          const data = await response.json();
          console.log("data", data);
          
            if (response.ok ){
            const requests = data.data.map((req) => ({
              id: req.request_id,
              transactionId: req.transaction_id || req.TransactionId,
              transaction: req.transaction_reference || "N/A",
              client: req.company_name || "N/A",
              property: req.property_name || "N/A",
              status: req.Request_Status || "In Progress",
              entityId: req.EntityId,
              created_at: req.created_at || "N/A",
              modified_at: req.modified_at || "N/A",
              Approved_At: req.Approved_At || "N/A",
              Approved_At: req.Approved_At || "N/A",
              propertyId: req.PropertyId,
              customer: req.customer_name || "N/A",
              created_by: req.created_by || "N/A",
              deligenceStatus: req.Deligence_status || "N/A",
              clientReferenceNumber: req.client_reference_number || "N/A",
              // Deligence_status: req.Deligence_status || "N/A",
            }));
console.log("requests", requests);

            setUserRequests(requests); // ✅ update the table
          setTotalRecords(data.pagination.total_items); // ✅ update pagination
          setTotalPages(data.pagination.total_pages);


          } else {
            console.error("Failed to filter user requests:", data);
          }
        } catch (error) {
          console.error("Error filtering requests:", error);
        }
      };
      
     
   
    
    
    const handleReset = () => {
        setFilter(initialFilter);
        setPage(1); // Reset to the first page
        // call the original fetchUserRequests
        fetchUserRequests();
      };

    const handleAddRequest = async () => {
        try {
          
            const response = await fetch(`${config["backend-url"]}/create_request`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`,
                },
                body: JSON.stringify({}),
            });

            const data = await response.json();

            if (response.ok) {
                const requestId = data.request_id;
                updateRequestId(requestId);
                navigate("/transaction-detail", { state: { requestId } });
            } else {
                console.error("Failed to create request", data);
            }
        } catch (error) {
            console.error("Error during API call:", error);
        }
    };

    const handlePageChange = (pageNumber) => {
        setPage(pageNumber);
    };
    const userRole = userSession.getUserType()
    // const userRole = localStorage.getItem("UserType");;
    return (
        <div className="dashboard">
            {/* <div className="flex justify-between items-center mb-4">
                <button className="add-request" onClick={handleAddRequest}>
                    Add New Request
                </button>
            </div> */}

            {/* ✅ Filter Section */}
            <div className="filter-container">
              <label>


                    
        <input
          type="text"
          placeholder="Request ID"
          value={filter.RequestID}
          onChange={(e) =>
            setFilter({ ...filter, RequestID: e.target.value })
          }
        />
      </label>
      <label>
        <input
          type="text"
          placeholder="Client Reference Number"
          value={filter.clientReferenceNumber}
          onChange={(e) =>
            setFilter({ ...filter, clientReferenceNumber: e.target.value })
          }
        />
      </label>
      <label>
        <input
          type="text"
          placeholder="Transaction Reference"
          value={filter.transaction_reference}
          onChange={(e) =>
            setFilter({ ...filter, transaction_reference: e.target.value })
          }
        />
      </label>
      <label>
        <input
          type="text"
          placeholder="Client"
          value={filter.client}
          onChange={(e) =>
            setFilter({ ...filter, client: e.target.value })
          }
        />
      </label>
      <label>
        <input
          type="text"
          placeholder="Property"
          value={filter.property}
          onChange={(e) =>
            setFilter({ ...filter, property: e.target.value })
          }
        />
      </label>

       <label>
        <input
          type="text"
          placeholder="Diligence Status"
          value={filter.DiligenceStatus}
          onChange={(e) =>
            setFilter({ ...filter, DiligenceStatus: e.target.value })
          }
        />
      </label>
      <label>
        <input
          type="text"
          placeholder="Request Status"
          value={filter.requestStatus}
          onChange={(e) =>
            setFilter({ ...filter, requestStatus: e.target.value })
          }
        />
      </label>
      <label>
        <input
          type="text"
          placeholder="Customer"
          value={filter.customer}
          onChange={(e) =>
            setFilter({ ...filter, customer: e.target.value })
          }
        />
      </label>
      <label>
        <input
          type="text"
          placeholder="Fee Earner"
          value={filter.Requestor}
          onChange={(e) =>
            setFilter({ ...filter, Requestor: e.target.value })
          }
        />
      </label>
</div>
<div style={{ display: "flex", gap: "10px", marginBottom: "1rem" }}>
    <button className="search-button add-request" onClick={handleReset}>Reset</button>
    <button className="search-button add-request" onClick={handleSearch}>Search</button>
</div>

<div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1rem" }}>
            {/* Sort Buttons */}
            <div className="sort-bar" style={{ marginBottom: 0 }}>
                <span className="sort-bar-label">Sort by:</span>
                <button className={`sort-btn${sortConfig && sortConfig.key === "id" ? " active" : ""}`} onClick={() => handleSort("id")}>
                    Request ID {getSortLabel("id")}
                </button>
                <button className={`sort-btn${sortConfig && sortConfig.key === "client" ? " active" : ""}`} onClick={() => handleSort("client")}>
                    Client/Counterparty {getSortLabel("client")}
                </button>
                <button className={`sort-btn${sortConfig && sortConfig.key === "clientReferenceNumber" ? " active" : ""}`} onClick={() => handleSort("clientReferenceNumber")}>
                    Client Reference Number {getSortLabel("clientReferenceNumber")}
                </button>
                <button className={`sort-btn${sortConfig && sortConfig.key === "created_at" ? " active" : ""}`} onClick={() => handleSort("created_at")}>
                    Date Opened {getSortLabel("created_at")}
                </button>
                {sortConfig && (
                    <button className="sort-btn" onClick={() => setSortConfig(null)} style={{ color: "#d9534f", borderColor: "#d9534f" }}>
                        ✕ Clear Sort
                    </button>
                )}
            </div>
  <div>
    <Select
      label={"Select Limit"}
      name={"limit"}
      value={limit}
      style={{ width: "100px" }}
      onChange={(e) => {
        if (e.target.value === "None") {
            setLimit(10);
        } else {
            setLimit(e.target.value);
        }
      }}
      options={[
        { value: "10", display_value: "10" },
        { value: "20", display_value: "20" },
        { value: "30", display_value: "30" },
        { value: "50", display_value: "50" },
        { value: "100", display_value: "100" },
      ]}
    />
  </div>
</div>

            {/* ✅ Requests Table */}
            <div className="recent-requests margin0">
            <table className="request-table all-request_table">
                <thead>
                    <tr>
                        <th>Request ID</th>
                        <th>Client Reference Number</th>
                        <th>Transaction Reference</th>
                        <th>Client/ Counterparty</th>
                        <th>Property</th>
                        <th>Diligence Status</th>
                        <th>Request Status</th>
                        <th>Opened Date</th>
                        <th>Last Modified On</th>
                        <th>Date of Report</th>

                        <th>Customer</th>
                        <th>Fee Earner</th>
                        <th>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {sortedRequests.map((row) => (
                        <tr key={row.id}>
                            <td>{row.id}</td>
                            <td>{row.clientReferenceNumber}</td>
                            <td>{row.transaction}</td>
                            <td data-entityid={row.entityId}>{row.client}</td>
                            <td data-propertyid={row.propertyId}>{row.property}</td>
                            <td>{row.deligenceStatus}</td>
                           
                            <td>
                                <span className={`status-tile ${getStatusClass(row.status)}`}>
                                    {row.status}
                                </span>
                            </td>
                            <td>{row.created_at}</td>
                            <td>{row.modified_at}</td>
                            <td>{row.Approved_At || "N/A"}</td>
                            <td>{row.customer}</td>
                            <td>{row.created_by}</td>
                            <td>
                                <div className="dropdown-container" ref={selectedDropdownId === row.id ? dropdownRef : null}>
                                    <button
                                        className="dropdown-btn"
                                        onClick={() => handleToggleDropdown(row.id)}
                                    >
                                        <BsThreeDotsVertical size={20} />
                                    </button>
                                    {selectedDropdownId === row.id && (
                                        <div className="custom-dropdown">
                                            {/* ✅ View Option */}
                                            <div
                                                className="dropdownItem"
                                                onClick={() => {

                                                    console.log("row", row);
                                                    sharedData.set("requestId", row.id);
                                                    sharedData.set("transactionId", row.transactionId);
                                                    sharedData.set("entityId", row.entityId);
                                                    sharedData.set("propertyId", row.propertyId);
                                                    sharedData.set("status", row.status);
                                                    primeDiligenceFromRow(row);
                                                    navigate("/transaction-detail?view=true&readonly=true");

                                                }}
                                            >
                                                View
                                            </div>

                                            {/* ✅ Edit Option - Only if status is not 'Completed' */}
                                            {row.status !== "Completed" && row.status !== "Complete"   && (
                                                <div
                                                    className="dropdownItem"
                                                    onClick={() => {
                                                        console.log("row", row);
                                                        sharedData.set("requestId", row.id);
                                                        sharedData.set("transactionId", row.transactionId);
                                                        sharedData.set("entityId", row.entityId);
                                                        sharedData.set("propertyId", row.propertyId);
                                                        sharedData.set("status", row.status);
                                                        primeDiligenceFromRow(row);

                                                        navigate("/transaction-detail?edit=true");
                                                    }}
                                                >
                                                    Edit
                                                </div>
                                            )}
                                            {(row.status === "Complete" || row.status === "Completed") && (
                                              <div
                                                className="dropdownItem"
                                                onClick={() => {
                                                  sharedData.set("requestId", row.id);
                                                  sharedData.set("transactionId", row.transactionId);
                                                  sharedData.set("entityId", row.entityId);
                                                  sharedData.set("propertyId", row.propertyId);
                                                  sharedData.set("status", row.status);
                                                  primeDiligenceFromRow(row);
                                                    navigate("/FinalReview");

                                                }}
                                              >
                                                Report
                                              </div>
                                            )}

                                            {/* ✅ Delete Option - Hide for already deleted requests */}
                                            {row.status !== "deleted" && (
                                                <div
                                                    className="dropdownItem"
                                                    onClick={() => {
                                                        setSelectedToDeleteRequestId(row.id);
                                                        setIsDeleteConfirmationModalOpen(true);
                                                        setSelectedDropdownId(null);
                                                    }}
                                                >
                                                    Delete
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
            <div className="table-summary">
    Showing {userRequests.length} requests of total {totalRecords} requests
    <br />
    Page {page} of {totalPages}
</div>


<div className="pagination">
    <button
        className="chevron"
        onClick={() => handlePageChange(page - 1)}
        disabled={page === 1}
    >
        <FaChevronLeft />
    </button>

    {Array.from({ length: totalPages }, (_, index) => index + 1)
        .filter((pageNum) => {
            if (totalPages <= 7) return true;
            const start = Math.max(1, page - 3);
            const end = Math.min(totalPages, page + 3);
            return pageNum >= start && pageNum <= end;
        })
        .map((pageNum) => (
            <button
                key={pageNum}
                onClick={() => handlePageChange(pageNum)}
                className={`page-button ${page === pageNum ? "active" : ""}`}
            >
                {pageNum}
            </button>
        ))}

    <button
        className="chevron"
        onClick={() => handlePageChange(page + 1)}
        disabled={page === totalPages}
    >
        <FaChevronRight />
    </button>
</div>
</div>

            <ConfirmationModalv2
                isOpen={isDeleteConfirmationModalOpen}
                heading="Delete Confirmation"
                message={`Are you sure you want to delete this Request (ID: ${selectedToDeleteRequestId})?`}
                onClose={() => {
                    setIsDeleteConfirmationModalOpen(false);
                    setSelectedToDeleteRequestId(null);
                }}
                confirmText="Delete"
                onConfirm={handleConfirmDelete}
                cancelText="Cancel"
            />
        </div>
    );
};

export default RequestTable;
